import { createSharedComposable } from '@vueuse/shared';
import type { AxiosRequestConfig } from 'axios';
import { useApiClient } from '@/app/core/api-client';
import { config } from '@/app/core/config';
import type {
  MissingItem,
  PaginatedResponse,
  PaginationQuery,
  ItemsListQuery,
  OffsetLimitQuery,
  SearchQuery,
  OffsetLimitResponse,
  SimpleItem,
  TaskStatus,
} from '@/app/shared/types';
import type {
  InventoryKpis,
  LocationKpis,
  SkuKpis,
  Inventory,
  Location,
  Sku,
  LocationDetails,
  InventoryDetails,
  InventoryReplenishmentScope,
  InventoryReplenishmentStatusUpdate,
  InventoryInventoriesSummaryData,
  InventoryLocationsSummaryData,
  InventorySkusSummaryData,
  ProductLocation,
  InventoryTransactionsDashboardData,
} from '../../interfaces';
import type { ShipmentWeekday } from '../../shared/shipment-days';

export const useInventoryApi = createSharedComposable(() => {
  const { transport, createMethod } = useApiClient();

  // Inventories

  function getInventoryKpis() {
    return transport.get<InventoryKpis>('/v1/inventories/kpis');
  }

  function getInventories(params?: PaginationQuery) {
    return transport.get<PaginatedResponse<Inventory>>('/v1/inventories', {
      params,
    });
  }

  function getInventoriesByIds(ids: string[]) {
    return transport.post<{ data: (Inventory | MissingItem)[] }>('/v1/inventories/by_ids', {
      ids,
    });
  }

  function getInventory(id: string) {
    return transport.get<InventoryDetails>(`/v1/inventories/${id}`);
  }

  // Locations

  function getLocationKpis() {
    return transport.get<LocationKpis>('/v1/locations/kpis');
  }

  function getLocations(params?: PaginationQuery) {
    return transport.get<PaginatedResponse<Location>>('/v1/locations', {
      params,
    });
  }

  function getLocationsByIds(ids: string[]) {
    return transport.post<{ data: (Location | MissingItem)[] }>('/v1/locations/by_ids', {
      ids,
    });
  }

  function getLocation(id: string) {
    return transport.get<LocationDetails>(`/v1/locations/${id}`);
  }

  // SKUs

  function getSkuKpis() {
    return transport.get<SkuKpis>('/v1/skus/kpis');
  }

  function getSkus(params?: PaginationQuery) {
    return transport.get<PaginatedResponse<Sku>>('/v1/skus', {
      params,
    });
  }

  function getSkuByIds(ids: string[]) {
    return transport.post<{ data: (Sku | MissingItem)[] }>('v1/skus/by_ids', {
      ids,
    });
  }

  // Replenishment

  function updateReplenishmentStatusByUpload(
    file: File,
    scope: InventoryReplenishmentScope,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress'],
  ) {
    return transport.post<{
      task_id: string;
    }>(
      '/v1/settings/avoid_replenishments/update/upload',
      { file, scope },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      },
    );
  }

  function updateReplenishmentStatusByQuery(payload: {
    query: string;
    scope: InventoryReplenishmentScope;
    active: boolean;
  }) {
    return transport.post<{
      task_id: string;
    }>('v1/settings/avoid_replenishments/query', payload);
  }

  function getReplenishmentStatusUpdates(params?: PaginationQuery & { scope: InventoryReplenishmentScope }) {
    return transport.get<PaginatedResponse<InventoryReplenishmentStatusUpdate>>(
      '/v1/settings/avoid_replenishments/update/status',
      {
        params,
      },
    );
  }

  function getReplenishmentStatusUpdate(id: string) {
    return transport.get<InventoryReplenishmentStatusUpdate>(`/v1/settings/avoid_replenishments/update/status/${id}`);
  }

  function cancelReplenishmentStatusUpdate(id: string) {
    return transport.put(`v1/tasks/abort/${id}`, {});
  }

  function updateStoreTransferExclusionsByUpload(
    file: File,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress'],
  ) {
    return transport.post<{
      task_id: string;
    }>(
      '/v1/settings/store_transfer_exclusions/update/upload',
      { file },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      },
    );
  }

  function getStoreTransferExclusionsStatusUpdates(params?: PaginationQuery) {
    return transport.get<PaginatedResponse<InventoryReplenishmentStatusUpdate>>(
      '/v1/settings/store_transfer_exclusions/update/status',
      {
        params,
      },
    );
  }

  function downloadStoreTransferExclusions(payload: { query: string }) {
    return transport.post<{
      task_id: string;
    }>('/v1/settings/store_transfer_exclusions/download', payload);
  }

  function updateMinMaxTargetsByUpload(file: File, onUploadProgress?: AxiosRequestConfig['onUploadProgress']) {
    return transport.post<{
      task_id: string;
    }>(
      '/v1/settings/min_max_targets/update/upload',
      { file },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      },
    );
  }

  function getMinMaxTargetsStatusUpdates(params?: PaginationQuery) {
    return transport.get<PaginatedResponse<InventoryReplenishmentStatusUpdate>>(
      '/v1/settings/min_max_targets/update/status',
      {
        params,
      },
    );
  }

  function downloadMinMaxTargets(payload: { query: string }) {
    return transport.post<{
      task_id: string;
    }>('/v1/settings/min_max_targets/download', payload);
  }

  function downloadReplenishmentStatus(payload: { query: string; scope: InventoryReplenishmentScope }) {
    return transport.post<{
      task_id: string;
    }>('v1/settings/avoid_replenishments/download', payload);
  }

  // Summary

  function getInventoriesSummary(query?: string) {
    return transport.get<InventoryInventoriesSummaryData>('/v1/settings/avoid_replenishments/summary', {
      params: {
        scope: 'inventories',
        query,
      },
    });
  }

  function getLocationsSummary(query?: string) {
    return transport.get<InventoryLocationsSummaryData>('/v1/settings/avoid_replenishments/summary', {
      params: {
        scope: 'locations',
        query,
      },
    });
  }

  function getSkusSummary(query?: string) {
    return transport.get<InventorySkusSummaryData>('/v1/settings/avoid_replenishments/summary', {
      params: {
        scope: 'skus',
        query,
      },
    });
  }

  function setProductLocationsStoreTransferStatusByIds(ids: string[], enabled: boolean) {
    return transport.patch('/v1/product_locations', { ids, is_store_transfer_enabled: enabled });
  }

  function setProductLocationsStoreTransferStatusByQuery(query: string, enabled: boolean) {
    return transport.patch<{ task_id: string }>('/v1/product_locations/by_query', {
      query,
      is_store_transfer_enabled: enabled,
    });
  }

  function getProductLocationsStoreTransferStatusTask(id: string) {
    return transport.get<{ state: TaskStatus }>(`/v1/tasks/${id}`);
  }

  function getProductLocationsIndexingStatus() {
    return transport.get<{ in_progress: true }>('/v1/product_locations/indexing_status');
  }

  // BFF

  function bffGetLocations(params?: ItemsListQuery<'full'>): Promise<OffsetLimitResponse<Location>>;
  function bffGetLocations(params: ItemsListQuery<'minimal'>): Promise<OffsetLimitResponse<SimpleItem>>;
  async function bffGetLocations(params?: ItemsListQuery<'full' | 'minimal'>) {
    const { data } = await transport.get<OffsetLimitResponse<Location | SimpleItem>>('/inventory/locations', {
      params,
      baseURL: config.bffUrl,
    });

    return data;
  }

  function bffUpdateShipmentDays(id: string, shipmentDays: ShipmentWeekday[]) {
    return transport.patch<{ shipment_days: ShipmentWeekday[] }>(
      `/inventory/locations/${id}/shipment_days`,
      { shipmentDays },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffBulkUpdateShipmentDays(locationIds: string[], shipmentDays: ShipmentWeekday[]) {
    return transport.patch<{ shipment_days: ShipmentWeekday[]; location_ids: string[] }>(
      '/inventory/locations/shipment_days/bulk',
      { shipmentDays, locationIds },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  // Select-all-matching: the BFF resolves the Meilisearch filter to every
  // matching location id before applying the bulk repoint.
  function bffBulkUpdateShipmentDaysByQuery(filter: string, shipmentDays: ShipmentWeekday[]) {
    return transport.patch<{ shipment_days: ShipmentWeekday[]; location_ids: string[] }>(
      '/inventory/locations/shipment_days/bulk',
      { shipmentDays, filter },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffUpdateLocationActiveStatus(id: string, status: boolean) {
    return transport.patch<{ status: boolean }>(
      `/inventory/locations/${id}/active_statuses`,
      { status },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  // Bulk by explicit ids.
  function bffBulkUpdateLocationActiveStatusesByIds(locationIds: string[], status: boolean) {
    return transport.patch<{ status: boolean; updated_count: number }>(
      '/inventory/locations/active_statuses/bulk',
      { status, locationIds },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  // Select-all-matching: the BFF resolves the Meilisearch filter to ids.
  function bffBulkUpdateLocationActiveStatusesByQuery(filter: string, status: boolean) {
    return transport.patch<{ status: boolean; updated_count: number }>(
      '/inventory/locations/active_statuses/bulk',
      { status, filter },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetLocationsByIds(ids?: string[]) {
    return transport.post<Location[]>(
      '/inventory/locations/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetInventories(params?: ItemsListQuery<'full'>): Promise<OffsetLimitResponse<Inventory>>;
  function bffGetInventories(params: ItemsListQuery<'minimal'>): Promise<OffsetLimitResponse<SimpleItem>>;
  async function bffGetInventories(params?: ItemsListQuery<'full' | 'minimal'>) {
    const { data } = await transport.get<OffsetLimitResponse<Inventory | SimpleItem>>('/inventory/inventories', {
      params,
      baseURL: config.bffUrl,
    });

    return data;
  }

  function bffGetInventoriesMinimal(params?: ItemsListQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/inventories', {
      params: {
        ...params,
        view: 'minimal',
      },
      baseURL: config.bffUrl,
    });
  }

  function bffGetSkus(params?: ItemsListQuery<'full'>): Promise<OffsetLimitResponse<Sku>>;
  function bffGetSkus(params: ItemsListQuery<'minimal'>): Promise<OffsetLimitResponse<SimpleItem>>;
  async function bffGetSkus(params?: ItemsListQuery<'full' | 'minimal'>) {
    const { data } = await transport.get<OffsetLimitResponse<Sku>>('/inventory/skus', {
      params,
      baseURL: config.bffUrl,
    });

    return data;
  }
  function bffGetSkusByIds(ids?: string[]) {
    return transport.post<Sku[]>(
      '/inventory/skus/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetProductLocations(params?: ItemsListQuery<'full'>): Promise<OffsetLimitResponse<ProductLocation>>;
  function bffGetProductLocations(params: ItemsListQuery<'minimal'>): Promise<OffsetLimitResponse<SimpleItem>>;
  async function bffGetProductLocations(params?: ItemsListQuery<'full' | 'minimal'>) {
    const { data } = await transport.get<OffsetLimitResponse<ProductLocation | SimpleItem>>(
      '/inventory/product-locations',
      {
        params,
        baseURL: config.bffUrl,
      },
    );

    return data;
  }

  const bffGetTransactionsDashboard = createMethod<
    [{ startDate: string; endDate: string; entityStatus: 'Active' | 'Avoid Replenishment'; clickhouse?: boolean }],
    InventoryTransactionsDashboardData
  >(async (transport, params) => {
    const { data } = await transport.get<InventoryTransactionsDashboardData>(
      '/analytics/dashboards/inventory-transactions',
      {
        params,
        baseURL: config.bffUrl,
      },
    );
    return data;
  });

  function bffGetStyles(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/styles', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetStylesByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/styles/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetSeasons(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/seasons', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetSeasonsByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/seasons/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetBrands(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/brands', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetBrandsByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/brands/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetMarkets(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/markets', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetColors(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/colors', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetClassifications(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/classifications', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetProductLifeCycleStages(params?: SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/product-life-cycle-stages', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetRegions(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/regions', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetCities(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/cities', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetDepartments(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/departments', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetDepartmentsByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/departments/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetCategories(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/categories', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetCategoriesByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/categories/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetAssortmentGroups(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/assortment-groups', {
      params,
      baseURL: config.bffUrl,
    });
  }

  const getAssortmentGroupsByIds = createMethod<[string[]], SimpleItem[]>(async (transport, ids) => {
    const { data } = await transport.post<SimpleItem[]>(
      '/inventory/assortment-groups/by-ids',
      {
        ids,
      },
      {
        baseURL: config.bffUrl,
      },
    );
    return data;
  });

  function bffGetSizes(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/sizes', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetSizesByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/sizes/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  function bffGetProducts(params?: OffsetLimitQuery & SearchQuery) {
    return transport.get<OffsetLimitResponse<SimpleItem>>('/inventory/products', {
      params,
      baseURL: config.bffUrl,
    });
  }

  function bffGetProductsByIds(ids?: string[]) {
    return transport.post<SimpleItem[]>(
      '/inventory/products/by-ids',
      { ids },
      {
        baseURL: config.bffUrl,
      },
    );
  }

  return {
    getInventory,
    getInventoryKpis,
    getInventories,
    getInventoriesByIds,
    getLocationKpis,
    getLocations,
    getLocationsByIds,
    getSkuKpis,
    getSkus,
    getSkuByIds,
    getLocation,
    downloadReplenishmentStatus,
    updateReplenishmentStatusByUpload,
    updateReplenishmentStatusByQuery,
    getReplenishmentStatusUpdates,
    getReplenishmentStatusUpdate,
    cancelReplenishmentStatusUpdate,
    updateStoreTransferExclusionsByUpload,
    getStoreTransferExclusionsStatusUpdates,
    downloadStoreTransferExclusions,
    updateMinMaxTargetsByUpload,
    getMinMaxTargetsStatusUpdates,
    downloadMinMaxTargets,
    getInventoriesSummary,
    getLocationsSummary,
    getSkusSummary,
    bffGetLocations,
    bffUpdateShipmentDays,
    bffBulkUpdateShipmentDays,
    bffBulkUpdateShipmentDaysByQuery,
    bffUpdateLocationActiveStatus,
    bffBulkUpdateLocationActiveStatusesByIds,
    bffBulkUpdateLocationActiveStatusesByQuery,
    bffGetInventories,
    bffGetSkus,
    bffGetProductLocations,
    setProductLocationsStoreTransferStatusByIds,
    setProductLocationsStoreTransferStatusByQuery,
    getProductLocationsStoreTransferStatusTask,
    getProductLocationsIndexingStatus,
    bffGetTransactionsDashboard,
    bffGetStyles,
    bffGetSeasons,
    bffGetBrands,
    bffGetMarkets,
    bffGetColors,
    bffGetClassifications,
    bffGetRegions,
    bffGetCities,
    bffGetDepartments,
    bffGetCategories,
    bffGetSizes,
    bffGetSizesByIds,
    bffGetLocationsByIds,
    bffGetSkusByIds,
    bffGetAssortmentGroups,
    bffGetDepartmentsByIds,
    bffGetStylesByIds,
    bffGetSeasonsByIds,
    bffGetBrandsByIds,
    bffGetCategoriesByIds,
    bffGetInventoriesMinimal,
    bffGetProducts,
    bffGetProductsByIds,
    getAssortmentGroupsByIds,
    bffGetProductLifeCycleStages,
  };
});
